export class MarkdownEditorUtils {
  /**
   * 获取文本区域元素
   * @param {VueComponent} component - Vue组件实例
   * @returns {HTMLTextAreaElement} 文本区域元素
   */
  static getTextArea(component) {
    return component.$el.querySelector('.editor-textarea');
  }

  /**
   * 获取选中文本信息
   * @param {HTMLTextAreaElement} textarea - 文本区域元素
   * @param {string} content - 编辑器内容
   * @returns {Object} 选中文本信息
   */
  static getSelectionInfo(textarea, content) {
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    
    return { start, end, selectedText };
  }

/**
 * 更新编辑器内容并设置光标位置
 * @param {VueComponent} component - Vue组件实例
 * @param {string} newContent - 新内容
 * @param {number} newStart - 新的光标开始位置
 * @param {number} newEnd - 新的光标结束位置
 */
static updateContentAndFocus(component, newContent, newStart, newEnd) {
  const textarea = this.getTextArea(component);
  if (!textarea) return;
  
  // 保存当前的滚动位置
  const scrollTop = textarea.scrollTop;
  const scrollLeft = textarea.scrollLeft;
  
  // 直接操作 DOM
  textarea.value = newContent;
  
  // 恢复滚动位置
  textarea.scrollTop = scrollTop;
  textarea.scrollLeft = scrollLeft;
  
  // 设置光标位置
  textarea.setSelectionRange(newStart, newEnd);
  
  // 聚焦
  textarea.focus();
  
  // 同步到 Vue 数据（可选，如果其他地方需要这个数据）
  component.editorContent = newContent;
}

/**
 * 插入目录占位符
 * @param {VueComponent} component - Vue组件实例
 */
static insertTableOfContents(component) {
  const textarea = this.getTextArea(component);
  if (!textarea) return;
  
  // 检查是否已经存在 [TOC]
  if (component.editorContent.trim().startsWith('[TOC]')) {
    // 如果已经存在，给出提示或直接返回
    console.log('目录占位符 [TOC] 已存在');
    return;
  }
  
  // 在内容开头插入 [TOC]
  let newContent = component.editorContent;
  let newCursorPos = 0;
  
  // 如果内容不为空且不以换行符开头，添加换行符
  if (newContent && !newContent.startsWith('\n')) {
    newContent = '[TOC]\n\n' + newContent;
    newCursorPos = 5; // '[TOC]' 的长度，光标定位在 ] 后面
  } else {
    newContent = '[TOC]\n' + newContent;
    newCursorPos = 5; // '[TOC]' 的长度，光标定位在 ] 后面
  }
  
  // 更新内容并将光标定位到 [TOC] 后面
  this.updateContentAndFocus(component, newContent, newCursorPos, newCursorPos);
}
  /**
   * 加粗文本
   * @param {VueComponent} component - Vue组件实例
   */
  static handleBold(component) {
    const textarea = this.getTextArea(component);
    if (!textarea) return;
    
    const { start, end, selectedText } = this.getSelectionInfo(textarea, component.editorContent);
    
    let newText;
    let newStart, newEnd;
    
    if (selectedText) {
      if (selectedText.startsWith('**') && selectedText.endsWith('**')) {
        newText = selectedText.substring(2, selectedText.length - 2);
        newStart = start;
        newEnd = start + newText.length;
      } else {
        newText = `**${selectedText}**`;
        newStart = start;
        newEnd = start + newText.length;
      }
    } else {
      newText = '****';
      newStart = start + 2;
      newEnd = start + 2;
    }
    
    const newContent = 
      component.editorContent.substring(0, start) + 
      newText + 
      component.editorContent.substring(end);
    
    this.updateContentAndFocus(component, newContent, newStart, newEnd);
  }

  /**
   * 斜体文本
   * @param {VueComponent} component - Vue组件实例
   */
  static handleItalic(component) {
    const textarea = this.getTextArea(component);
    if (!textarea) return;
    
    const { start, end, selectedText } = this.getSelectionInfo(textarea, component.editorContent);
    
    let newText;
    let newStart, newEnd;
    
    if (selectedText) {
      if (selectedText.startsWith('*') && selectedText.endsWith('*') && 
          !(selectedText.startsWith('**') && selectedText.endsWith('**'))) {
        newText = selectedText.substring(1, selectedText.length - 1);
        newStart = start;
        newEnd = start + newText.length;
      } else {
        newText = `*${selectedText}*`;
        newStart = start;
        newEnd = start + newText.length;
      }
    } else {
      newText = '**';
      newStart = start + 1;
      newEnd = start + 1;
    }
    
    const newContent = 
      component.editorContent.substring(0, start) + 
      newText + 
      component.editorContent.substring(end);
    
    this.updateContentAndFocus(component, newContent, newStart, newEnd);
  }

  /**
   * 下划线文本
   * @param {VueComponent} component - Vue组件实例
   */
  static handleUnderline(component) {
    const textarea = this.getTextArea(component);
    if (!textarea) return;
    
    const { start, end, selectedText } = this.getSelectionInfo(textarea, component.editorContent);
    
    let newText;
    let newStart, newEnd;
    
    if (selectedText) {
      if (selectedText.startsWith('<u>') && selectedText.endsWith('</u>')) {
        newText = selectedText.substring(3, selectedText.length - 4);
        newStart = start;
        newEnd = start + newText.length;
      } else {
        newText = `<u>${selectedText}</u>`;
        newStart = start;
        newEnd = start + newText.length;
      }
    } else {
      newText = '<u></u>';
      newStart = start + 3;
      newEnd = start + 3;
    }
    
    const newContent = 
      component.editorContent.substring(0, start) + 
      newText + 
      component.editorContent.substring(end);
    
    this.updateContentAndFocus(component, newContent, newStart, newEnd);
  }

  /**
   * 删除线文本
   * @param {VueComponent} component - Vue组件实例
   */
  static handleStrikethrough(component) {
    const textarea = this.getTextArea(component);
    if (!textarea) return;
    
    const { start, end, selectedText } = this.getSelectionInfo(textarea, component.editorContent);
    
    let newText;
    let newStart, newEnd;
    
    if (selectedText) {
      if (selectedText.startsWith('~~') && selectedText.endsWith('~~')) {
        newText = selectedText.substring(2, selectedText.length - 2);
        newStart = start;
        newEnd = start + newText.length;
      } else {
        newText = `~~${selectedText}~~`;
        newStart = start;
        newEnd = start + newText.length;
      }
    } else {
      newText = '~~~~';
      newStart = start + 2;
      newEnd = start + 2;
    }
    
    const newContent = 
      component.editorContent.substring(0, start) + 
      newText + 
      component.editorContent.substring(end);
    
    this.updateContentAndFocus(component, newContent, newStart, newEnd);
  }

  /**
   * 处理标题
   * @param {VueComponent} component - Vue组件实例
   */
  static handleHeading(component) {
    const textarea = this.getTextArea(component);
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    // 找到当前行的开始和结束位置
    let lineStart = start;
    let lineEnd = end;
    
    while (lineStart > 0 && component.editorContent[lineStart - 1] !== '\n') {
      lineStart--;
    }
    
    while (lineEnd < component.editorContent.length && component.editorContent[lineEnd] !== '\n') {
      lineEnd++;
    }
    
    const currentLine = component.editorContent.substring(lineStart, lineEnd);
    const trimmedLine = currentLine.trimStart();
    const leadingSpaces = currentLine.length - trimmedLine.length;
    
    let newText;
    let newStart, newEnd;
    
    if (trimmedLine.startsWith('# ')) {
      newText = trimmedLine.substring(2);
      newStart = lineStart + leadingSpaces;
      newEnd = newStart + newText.length;
    } else if (trimmedLine.startsWith('## ')) {
      newText = '# ' + trimmedLine.substring(3);
      newStart = lineStart + leadingSpaces;
      newEnd = newStart + newText.length;
    } else if (trimmedLine.startsWith('### ')) {
      newText = '## ' + trimmedLine.substring(4);
      newStart = lineStart + leadingSpaces;
      newEnd = newStart + newText.length;
    } else if (trimmedLine.startsWith('#### ')) {
      newText = '### ' + trimmedLine.substring(5);
      newStart = lineStart + leadingSpaces;
      newEnd = newStart + newText.length;
    } else if (trimmedLine.startsWith('##### ')) {
      newText = '#### ' + trimmedLine.substring(6);
      newStart = lineStart + leadingSpaces;
      newEnd = newStart + newText.length;
    } else if (trimmedLine.startsWith('###### ')) {
      newText = '##### ' + trimmedLine.substring(7);
      newStart = lineStart + leadingSpaces;
      newEnd = newStart + newText.length;
    } else {
      newText = '# ' + trimmedLine;
      newStart = lineStart + leadingSpaces;
      newEnd = newStart + newText.length;
    }
    
    const newContent = 
      component.editorContent.substring(0, lineStart) + 
      newText + 
      component.editorContent.substring(lineEnd);
    
    this.updateContentAndFocus(component, newContent, newStart, newEnd);
  }

    /**
     * 处理无序列表
     * @param {VueComponent} component - Vue组件实例
     */
    static handleUnorderedList(component) {
    const textarea = this.getTextArea(component);
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    
    // 找到当前行的开始位置
    let lineStart = start;
    while (lineStart > 0 && component.editorContent[lineStart - 1] !== '\n') {
        lineStart--;
    }
    
    // 生成无序列表示例
    const unorderedListExample = 
        `- 第一项\n` +
        `- 第二项\n` +
        `- 第三项`;
    
    const newContent = 
        component.editorContent.substring(0, lineStart) + 
        unorderedListExample + 
        component.editorContent.substring(lineStart);
    
    // 将光标定位到第一个列表项的内容位置
    const newCursorPos = lineStart + 2; // "- " 后面的位置
    
    this.updateContentAndFocus(component, newContent, newCursorPos, newCursorPos);
    }

    /**
     * 处理有序列表
     * @param {VueComponent} component - Vue组件实例
     */
    static handleOrderedList(component) {
    const textarea = this.getTextArea(component);
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    
    // 找到当前行的开始位置
    let lineStart = start;
    while (lineStart > 0 && component.editorContent[lineStart - 1] !== '\n') {
        lineStart--;
    }
    
    // 生成有序列表示例
    const orderedListExample = 
        `1. 第一项\n` +
        `2. 第二项\n` +
        `3. 第三项`;
    
    const newContent = 
        component.editorContent.substring(0, lineStart) + 
        orderedListExample + 
        component.editorContent.substring(lineStart);
    
    // 将光标定位到第一个列表项的内容位置
    const newCursorPos = lineStart + 3; // "1. " 后面的位置
    
    this.updateContentAndFocus(component, newContent, newCursorPos, newCursorPos);
    }
    /**
     * 插入链接
     * @param {VueComponent} component - Vue组件实例
     */
    static handleLink(component) {
        const textarea = this.getTextArea(component);
        if (!textarea) return;
        
        const { start, end, selectedText } = this.getSelectionInfo(textarea, component.editorContent);
        
        let newText;
        let newStart, newEnd;
        
        if (selectedText) {
        newText = `[${selectedText}](url)`;
        newStart = start + newText.length - 4;
        newEnd = start + newText.length - 1;
        } else {
        newText = `[](url)`;
        newStart = start + 1;
        newEnd = start + 1;
        }
        
        const newContent = 
        component.editorContent.substring(0, start) + 
        newText + 
        component.editorContent.substring(end);
        
        this.updateContentAndFocus(component, newContent, newStart, newEnd);
    }

    /**
     * 插入代码块
     * @param {VueComponent} component - Vue组件实例
     */
    static handleCode(component) {
    const textarea = this.getTextArea(component);
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    
    // 找到当前行的开始位置
    let lineStart = start;
    while (lineStart > 0 && component.editorContent[lineStart - 1] !== '\n') {
        lineStart--;
    }
    
    // 生成代码块示例
    const codeBlockExample = 
        '```javascript\n' +
        'function helloWorld() {\n' +
        '  console.log("Hello, World!");\n' +
        '}\n' +
        '```';
    
    const newContent = 
        component.editorContent.substring(0, lineStart) + 
        codeBlockExample + 
        component.editorContent.substring(lineStart);
    
    // 将光标定位到代码块内部的第一行，方便用户直接编辑
    const newCursorPos = lineStart + 14; // "```javascript\n" 后面的位置
    
    this.updateContentAndFocus(component, newContent, newCursorPos, newCursorPos);
    }

  /**
   * 插入表格
   * @param {VueComponent} component - Vue组件实例
   */
  static handleTable(component) {
    const textarea = this.getTextArea(component);
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    const tableTemplate = 
      `| 列1 | 列2 | 列3 |\n` +
      `| --- | --- | --- |\n` +
      `| 内容1 | 内容2 | 内容3 |\n` +
      `| 内容4 | 内容5 | 内容6 |`;
    
    const newText = tableTemplate;
    const newStart = start + 2;
    const newEnd = start + 7;
    
    const newContent = 
      component.editorContent.substring(0, start) + 
      newText + 
      component.editorContent.substring(end);
    
    this.updateContentAndFocus(component, newContent, newStart, newEnd);
  }

  /**
   * 插入引用
   * @param {VueComponent} component - Vue组件实例
   */
  static handleQuote(component) {
    const textarea = this.getTextArea(component);
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    
    let lineStart = start;
    let lineEnd = end;
    
    while (lineStart > 0 && component.editorContent[lineStart - 1] !== '\n') {
      lineStart--;
    }
    
    while (lineEnd < component.editorContent.length && component.editorContent[lineEnd] !== '\n') {
      lineEnd++;
    }
    
    const currentLine = component.editorContent.substring(lineStart, lineEnd);
    const trimmedLine = currentLine.trimStart();
    
    let newText;
    let newStart, newEnd;
    
    if (trimmedLine.startsWith('> ')) {
      newText = trimmedLine.substring(2);
      newStart = lineStart + (currentLine.length - trimmedLine.length);
      newEnd = newStart + newText.length;
    } else {
      newText = '> ' + trimmedLine;
      newStart = lineStart + (currentLine.length - trimmedLine.length);
      newEnd = newStart + newText.length;
    }
    
    const newContent = 
      component.editorContent.substring(0, lineStart) + 
      newText + 
      component.editorContent.substring(lineEnd);
    
    this.updateContentAndFocus(component, newContent, newStart, newEnd);
  }

  /**
   * 插入水平线
   * @param {VueComponent} component - Vue组件实例
   */
  static handleHorizontalRule(component) {
    const textarea = this.getTextArea(component);
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    
    let lineStart = start;
    
    while (lineStart > 0 && component.editorContent[lineStart - 1] !== '\n') {
      lineStart--;
    }
    
    const newText = '---\n';
    const newEnd = lineStart + newText.length;
    
    const newContent = 
      component.editorContent.substring(0, lineStart) + 
      newText + 
      component.editorContent.substring(lineStart);
    
    this.updateContentAndFocus(component, newContent, newEnd, newEnd);
  }

/**
 * 插入行内数学公式
 * @param {VueComponent} component - Vue组件实例
 */
static handleInlineMath(component) {
  const textarea = this.getTextArea(component);
  if (!textarea) return;
  
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  
  // 生成行内数学公式示例
  const mathExample = `$E = mc^2$`;
  
  const newContent = 
    component.editorContent.substring(0, start) + 
    mathExample + 
    component.editorContent.substring(end);
  
  // 将光标定位到公式内部，方便用户直接编辑
  const newCursorPos = start + 1; // "$" 后面的位置
  
  this.updateContentAndFocus(component, newContent, newCursorPos, newCursorPos);
}

/**
 * 插入块级数学公式
 * @param {VueComponent} component - Vue组件实例
 */
static handleBlockMath(component) {
  const textarea = this.getTextArea(component);
  if (!textarea) return;
  
  const start = textarea.selectionStart;
  
  // 找到当前行的开始位置
  let lineStart = start;
  while (lineStart > 0 && component.editorContent[lineStart - 1] !== '\n') {
    lineStart--;
  }
  
  // 生成块级数学公式示例
  const blockMathExample = 
    `$$\n` +
    `\\int_{0}^{\\infty} e^{-x^2} dx = \\frac{\\sqrt{\\pi}}{2}\n` +
    `$$`;
  
  const newContent = 
    component.editorContent.substring(0, lineStart) + 
    blockMathExample + 
    component.editorContent.substring(lineStart);
  
  // 将光标定位到公式内部的第一行，方便用户直接编辑
  const newCursorPos = lineStart + 4; // "$$\n" 后面的位置
  
  this.updateContentAndFocus(component, newContent, newCursorPos, newCursorPos);
}

/**
 * 插入图片
 * @param {VueComponent} component - Vue组件实例
 * @param {string} imgUrl - 图片URL
 */
static insertImg(component, imgUrl) {
  const textarea = this.getTextArea(component);
  if (!textarea) return;
  
  const start = textarea.selectionStart;
  
  // 找到当前行的开始位置
  let lineStart = start;
  while (lineStart > 0 && component.editorContent[lineStart - 1] !== '\n') {
    lineStart--;
  }
  
  // 找到当前行的结束位置
  let lineEnd = start;
  while (lineEnd < component.editorContent.length && component.editorContent[lineEnd] !== '\n') {
    lineEnd++;
  }
  
  // 如果当前行有内容，则在下一行插入图片
  let insertPosition = lineEnd;
  let prefix = '\n';
  
  // 如果当前行已经是空行或者文件开头，则不需要额外的换行
  if (lineStart === lineEnd) {
    prefix = '';
  }
  
  const imageMarkdown = `${prefix}![image](${imgUrl})\n`;
  
  const newContent = 
    component.editorContent.substring(0, insertPosition) + 
    imageMarkdown + 
    component.editorContent.substring(insertPosition);
  
  // 将光标定位到插入图片后的位置
  const newCursorPos = insertPosition + imageMarkdown.length;
  
  this.updateContentAndFocus(component, newContent, newCursorPos, newCursorPos);
}
}